// Internal
import type { BaseAddressFieldResults } from '@/store';
import { appStore } from '@/store';
import { getLocale } from '@/lib/globals';
import { isFunction } from '@/lib/utils/typeUtils';
import type { EmptyObject, MappedAddressValues, ShippingOption } from './types';
import type { SimpleShippingAddress } from '@/store/models/form/types';
import type { SimpleShippingAddressString } from '@/store/models/api/types';
import type { CountryCodes } from '@/generated/graphql';
import { logShippingCallbacksInfoToFPTI } from '@/lib/utils/shipping/fpti';

/**
 *
 * @description The address fields in the easyPeasy store are objects that
 *  contains other properties. Ex:
 *    city: {
 *      displayError: false
 *      errorCode: "valueMissing"
 *      isDirty: false
 *      isValid: false
 *      value: ""
 *    }
 *
 *  In some cases, we only care about the field's value; hence, this
 *  function will map the address fields to their values only. EX:
 *    billingAddress: {
 *      city: "San Jose",
 *      country: "US"
 *    }
 *
 * @NOTE be aware that fields that don't have a value property,
 * won't be part of the mapped address
 * @param {Object} address
 *
 * @returns {Object}
 */
const mapAddressValues = (
  address: BaseAddressFieldResults | EmptyObject = {},
): MappedAddressValues | EmptyObject => {
  return Object.keys(address).reduce<MappedAddressValues | EmptyObject>(
    (mappedAddress, addressField) => {
      const addressFieldDetails =
        address[addressField as keyof BaseAddressFieldResults] || {};
      if (addressFieldDetails.hasOwnProperty('value')) {
        mappedAddress[addressField as keyof MappedAddressValues] =
          addressFieldDetails?.value as any; // Trust that country will be a valid Country Code
      }
      return mappedAddress;
    },
    {},
  );
};

/**
 * @param {boolean} isSntSupported
 *
 * @returns {string} returns the Shipping and Tax integration type
 */
const getShippingMethodsVariant = (isSntSupported = false): string => {
  // Indicates V2 SDK Shipping callbacks which were introduced
  // with AXO checkout integrations.
  logShippingCallbacksInfoToFPTI();
  if (
    isFunction(window?.xprops?.onShippingAddressChange) &&
    isFunction(window?.xprops?.onShippingOptionsChange)
  ) {
    return 'viaV2SDKShippingMethods';
  }

  // Indicates Legacy SDK Shipping callbacks integrations.
  if (isFunction(window?.xprops?.onShippingChange)) {
    return 'viaV1SDKShippingMethods';
  }

  // Indicates non SDK shipping methods integrations.
  if (isSntSupported) {
    return 'viaNonSDKShippingMethods';
  }

  // for all the non shipping method flows
  return 'none';
};

const getShippingCountry = (): CountryCodes => {
  const { form: formState } = appStore.getState();
  const { shipToBilling, shippingAddress } = formState;
  return shipToBilling.value
    ? getLocale().country
    : shippingAddress.country.value;
};

/**
 * @description If the shipToBilling checkbox is checked, it returns
 *   the billing address; otherwise, it returns the shipping address.
 *
 * @returns {Object} Shipping address
 */
const getShippingAddress = (): BaseAddressFieldResults => {
  const { form: formState } = appStore.getState();
  const { billingAddress, shipToBilling, shippingAddress } = formState;

  return shipToBilling.value ? billingAddress : shippingAddress;
};

const trimOrEmpty = (str?: string | null): string => {
  return str ? str.trim() : '';
};

export const getSimpleShippingAddressString = (
  address: SimpleShippingAddress,
): SimpleShippingAddressString => {
  return `${trimOrEmpty(address.line1)}; ${
    trimOrEmpty(address.line2) ? trimOrEmpty(address.line2) + '; ' : ''
  }${trimOrEmpty(address.city)}, ${trimOrEmpty(address.state)} ${trimOrEmpty(
    address.postalCode,
  )}; ${trimOrEmpty(address.country)}` as SimpleShippingAddressString;
};

export const simplifyShippingAddress = (
  shippingAddressFields: BaseAddressFieldResults,
  country: CountryCodes,
): SimpleShippingAddress => {
  const {
    line1: { value: line1 },
    line2: { value: line2 },
    city: { value: city },
    state: { value: state },
    postalCode: { value: postalCode },
  } = shippingAddressFields;

  return {
    city,
    country,
    line1,
    line2,
    postalCode,
    state,
  };
};

const getSelectedShippingMethod = (
  shippingMethods: ShippingOption[],
): ShippingOption | undefined => {
  return shippingMethods?.find(method => method?.selected);
};

export {
  getSelectedShippingMethod,
  getShippingAddress,
  getShippingCountry,
  getShippingMethodsVariant,
  mapAddressValues,
};

export {
  addressAreEqual,
  buildAmountBreakdown,
  currencyMapper,
  normalizeAddressForMerchant,
  normalizeShippingOption,
} from './v1';

export {
  addressAreEqualV2,
  buildAmountBreakdownV2,
  currencyMapperV2,
  normalizeAddressForMerchantV2,
  normalizeShippingOptionV2,
} from './v2';
